import React, { useState, useCallback } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import HondaLoader from '../../components/common/HondaLoader';
import { sanitizeInput } from '../../utils/inputValidation';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const getCesionesTracking = async (grupo, orden, sellerID = '') => {
  const params = new URLSearchParams();
  if (grupo) params.append('grupo', grupo);
  if (orden) params.append('orden', orden);
  //if (cessionID) params.append('cessionID', cessionID);
  if (sellerID) params.append('sellerID', sellerID);
  const response = await fetch(
    `${API_BASE_URL}/api/Cesiones/getCessionTracking?${params.toString()}`,
    { method: 'GET', headers: { Accept: 'application/json' } }
  );
  if (!response.ok) throw new Error(`Error ${response.status}`);
  return response.json();
};

const determinarCategoria = (desc) => {
  const d = (desc || '').toUpperCase();
  if (d.includes('APROBADO') || d.includes('COMPLETADO')) return 'success';
  if (d.includes('RECHAZADO') || d.includes('ERROR')) return 'error';
  if (d.includes('PENDIENTE') || d.includes('REVISION') || d.includes('INCOMPLETO')) return 'warning';
  return 'info';
};

const formatearFecha = (fechaStr) => {
  try {
    const [fecha, hora] = fechaStr.split(' ');
    if (!fecha) return fechaStr;
    const [year, month, day] = fecha.split('-');
    return `${day}/${month}/${year} ${hora || ''}`.trim();
  } catch { return fechaStr; }
};

const calcularTiempoTotal = (eventos) => {
  if (eventos.length < 2) return 'N/A';
  try {
    const primero = new Date(eventos[eventos.length - 1].eventDate);
    const ultimo = new Date(eventos[0].eventDate);
    const diffMs = ultimo - primero;
    const dias = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const horas = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const min = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    if (dias > 0) return `${dias}d ${horas}h ${min}min`;
    if (horas > 0) return `${horas}h ${min}min`;
    return `${min} min`;
  } catch { return 'N/A'; }
};

const getCategoriaColor = (cat) => {
  if (cat === 'success') return 'text-green-600 bg-green-50 border-green-200';
  if (cat === 'warning') return 'text-yellow-600 bg-yellow-50 border-yellow-200';
  if (cat === 'error') return 'text-red-600 bg-red-50 border-red-200';
  return 'text-blue-600 bg-blue-50 border-blue-200';
};

const getCategoriaIcono = (cat) => {
  if (cat === 'success') return 'check_circle';
  if (cat === 'warning') return 'warning';
  if (cat === 'error') return 'error';
  return 'info';
};

const TrackingCesiones = ({ user, onLogout, onNavigate }) => {
  const [grupo, setGrupo] = useState('');
  const [orden, setOrden] = useState('');
  const [cessionID, setCessionID] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [resumen, setResumen] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [error, setError] = useState(null);

  const handleBuscar = useCallback(async () => {
    if (!grupo && !orden && !cessionID) { setError('Complete al menos un campo de busqueda'); return; }
    setBuscando(true);
    setError(null);
    try {
      const data = await getCesionesTracking(grupo, orden, user.ce_ID ? user.ce_ID : '0');
      if (!data || data.length === 0) {
        setError('No se encontraron eventos para los datos especificados');
        setResumen(null); setEventos([]);
        return;
      }
      const ordenados = [...data].sort((a, b) => new Date(b.eventDate) - new Date(a.eventDate));
      const mapeados = ordenados.map((ev, i) => ({
        id: i + 1,
        evento: ev.eventDescription,
        fecha: formatearFecha(ev.eventDate),
        usuario: ev.userName,
        detalle: ev.notes || 'Sin notas',
        categoria: determinarCategoria(ev.eventDescription),
      }));
      const ultimo = ordenados[0];
      setResumen({
        grupo: data[0].group, orden: data[0].order,
        estado: ultimo.eventDescription,
        totalEventos: data.length,
        ultimaActualizacion: formatearFecha(ultimo.eventDate),
        tiempoTotal: calcularTiempoTotal(ordenados),
      });
      setEventos(mapeados);
    } catch (err) {
      setError(err.message || 'Error al obtener el tracking de eventos');
      setResumen(null); setEventos([]);
    } finally {
      setBuscando(false);
    }
  }, [grupo, orden, cessionID]);

  const handleKeyDown = useCallback(e => { if (e.key === 'Enter') handleBuscar(); }, [handleBuscar]);

  return (
    <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
      <div className="max-w-full mx-auto px-3 py-4">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-1.5 h-8 gradient-red rounded-full shadow-glow-red"></div>
            <h2 className="text-2xl font-bold text-gradient">Tracking de Cesiones</h2>
          </div>
          <p className="text-gray-500 text-sm ml-5">Historial de eventos de una cesion</p>
        </div>

        <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-2xl shadow-lg p-6 mb-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-red-100 to-transparent rounded-full opacity-20 blur-3xl"></div>
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div className="group">
              <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Grupo</label>
              <input type="text" placeholder="Ej: 010982" value={grupo}
                onChange={e => setGrupo(sanitizeInput(e.target.value))} onKeyDown={handleKeyDown}
                className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all"
              />
            </div>
            <div className="group">
              <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Orden</label>
              <input type="text" placeholder="Ej: 0106" value={orden}
                onChange={e => setOrden(sanitizeInput(e.target.value))} onKeyDown={handleKeyDown}
                className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all"
              />
            </div>
            {/* Optional Cession ID field */}
            <div className="group" style={{ display: 'none' }}>
              <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Cession ID</label>
              <input type="text" placeholder="Ej: 12345" value={cessionID}
                onChange={e => setCessionID(sanitizeInput(e.target.value))} onKeyDown={handleKeyDown}
                className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all"
              />
            </div>
            <div>
              <button onClick={handleBuscar} disabled={buscando}
                className="w-full px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl transition-all text-sm font-semibold shadow-lg shadow-red-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
              >
                <span className="material-icons text-sm">{buscando ? 'hourglass_empty' : 'search'}</span>
                {buscando ? 'Buscando...' : 'Buscar Cesion'}
              </button>
            </div>
          </div>
        </div>

        {error && !buscando && (
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl shadow-lg p-4 mb-8 flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="material-icons text-red-600 text-xl">error_outline</span>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-red-800 mb-1">Error al buscar</h3>
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="w-8 h-8 bg-red-100 hover:bg-red-200 rounded-lg flex items-center justify-center">
              <span className="material-icons text-red-600 text-sm">close</span>
            </button>
          </div>
        )}

        {buscando && (
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8 mb-8">
            <HondaLoader variant="inline" message="Buscando eventos..." size="large" />
          </div>
        )}

        {resumen && !buscando && (
          <div className="space-y-6 animate-fade-in">
            {/* Resumen */}
            <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-2xl shadow-lg p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Grupo', value: resumen.grupo, icon: 'tag' },
                  { label: 'Orden', value: resumen.orden, icon: 'receipt_long' },
                  { label: 'Total Eventos', value: resumen.totalEventos, icon: 'timeline' },
                  { label: 'Ultima Actualizacion', value: resumen.ultimaActualizacion, icon: 'schedule' },
                ].map(({ label, value, icon }) => (
                  <div key={label} className="text-center">
                    <span className="material-icons text-red-600 text-xl mb-1 block">{icon}</span>
                    <p className="text-xs text-gray-500 mb-1">{label}</p>
                    <p className="text-sm font-bold text-gray-800">{value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4">
                <p className="text-white font-bold">Historial de Eventos ({eventos.length})</p>
              </div>
              <div className="divide-y divide-gray-100">
                {eventos.map((ev, i) => (
                  <div key={ev.id} className="p-4 hover:bg-gray-50 transition-colors flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full border flex items-center justify-center flex-shrink-0 ${getCategoriaColor(ev.categoria)}`}>
                        <span className="material-icons text-sm">{getCategoriaIcono(ev.categoria)}</span>
                      </div>
                      {i < eventos.length - 1 && <div className="w-px flex-1 bg-gray-200 mt-1"></div>}
                    </div>
                    <div className="flex-1 min-w-0 pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold text-gray-800">{ev.evento}</p>
                        <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">{ev.fecha}</span>
                      </div>
                      {/*Ocultar el usuario para el concesionario*/}
                      {user.group_ID != "1" && (
                        <p className="text-xs text-gray-500 mt-0.5">Usuario: {ev.usuario}</p>
                      )}
                      
                      {ev.detalle && ev.detalle !== 'Sin notas' && (
                        <p className="text-xs text-gray-500 mt-1 bg-gray-50 rounded px-2 py-1">{ev.detalle}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default TrackingCesiones;